import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { ChatHistoryGroup, ChatSummary } from '../types';

/**
 * Panel de conversaciones guardadas.
 *
 * Es puramente de presentación: recibe el listado ya resuelto y avisa qué
 * quiere hacer el usuario. Quien lo usa se encarga de hablar con el storage,
 * así el panel sirve igual para el chat en pantalla completa que para
 * cualquier otra pantalla que quiera ofrecer el historial.
 */
@Component({
  selector: 'app-chat-history',
  templateUrl: './chat-history.component.html',
  styleUrls: ['./chat-history.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ChatHistoryComponent {

  /** Conversaciones a listar, de la más reciente a la más antigua */
  @Input() chats: ChatSummary[] = [];
  /** Conversación abierta, se resalta en la lista */
  @Input() activeId: string | null = null;
  /** Se está leyendo el historial del storage */
  @Input() loading: boolean = false;
  /** Hay un turno en curso: no se puede cambiar de conversación */
  @Input() busy: boolean = false;

  /** El usuario eligió una conversación */
  @Output() openChat: EventEmitter<ChatSummary> = new EventEmitter();
  /** El usuario quiere empezar una conversación nueva */
  @Output() newChat: EventEmitter<void> = new EventEmitter();
  /** El usuario renombró una conversación */
  @Output() renameChat: EventEmitter<{ chat: ChatSummary; title: string }> = new EventEmitter();
  /** El usuario quiere eliminar una conversación */
  @Output() deleteChat: EventEmitter<ChatSummary> = new EventEmitter();
  /** El usuario plegó el panel */
  @Output() closePanel: EventEmitter<void> = new EventEmitter();

  /** Filtro por nombre */
  public search: string = '';
  /** Conversación que se está renombrando */
  public renamingId: string | null = null;
  /** Nombre que el usuario escribe al renombrar */
  public renameText: string = '';

  /**
   * Caja de renombrado: al aparecer toma el foco y selecciona el nombre actual,
   * para que se pueda escribir directo sin otro clic.
   */
  @ViewChild('renameInput')
  set renameInput(input: ElementRef<HTMLInputElement> | undefined) {
    if (!input) return;

    setTimeout(() => {
      input.nativeElement.focus();
      input.nativeElement.select();
    });
  }

  /**
   * Constructor
   * @param {TranslateService} _translateService
   */
  constructor(private _translateService: TranslateService) { }

  /**
   * Conversaciones agrupadas por antigüedad, ya filtradas por el buscador.
   * @returns {ChatHistoryGroup[]} Devuelve solo los grupos que tienen contenido
   */
  get groups(): ChatHistoryGroup[] {
    const search = (this.search || '').trim().toLowerCase();

    const chats = search
      ? (this.chats ?? []).filter(c => (c.title || '').toLowerCase().includes(search)
        || (c.preview || '').toLowerCase().includes(search))
      : (this.chats ?? []);

    const groups: ChatHistoryGroup[] = [
      { label: this.label('Hoy', 'Today'), chats: [] },
      { label: this.label('Ayer', 'Yesterday'), chats: [] },
      { label: this.label('Últimos 7 días', 'Last 7 days'), chats: [] },
      { label: this.label('Anteriores', 'Older'), chats: [] }
    ];

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const day = 24 * 60 * 60 * 1000;

    chats.forEach(chat => {
      const diff = startOfToday.getTime() - new Date(chat.updatedAt).getTime();

      if (diff <= 0) groups[0].chats.push(chat);
      else if (diff <= day) groups[1].chats.push(chat);
      else if (diff <= 6 * day) groups[2].chats.push(chat);
      else groups[3].chats.push(chat);
    });

    return groups.filter(g => g.chats.length > 0);
  }

  /**
   * Indica si no hay nada que listar una vez aplicado el buscador.
   */
  get empty(): boolean {
    return !this.loading && this.groups.length === 0;
  }

  /**
   * Abre una conversación, salvo que ya sea la activa o haya un turno en curso.
   * @param {ChatSummary} chat Conversación elegida
   */
  select(chat: ChatSummary): void {
    if (this.busy || this.renamingId === chat.id || chat.id === this.activeId) return;

    this.openChat.emit(chat);
  }

  /**
   * Abre la caja para cambiarle el nombre a una conversación.
   * @param {ChatSummary} chat Conversación a renombrar
   * @param {Event} event Evento del clic, para no abrir la conversación
   */
  startRename(chat: ChatSummary, event?: Event): void {
    event?.stopPropagation();

    this.renamingId = chat.id;
    this.renameText = chat.title;
  }

  /**
   * Cierra la caja de renombrado y avisa el nombre nuevo, si cambió.
   * @param {ChatSummary} chat Conversación renombrada
   */
  commitRename(chat: ChatSummary): void {
    const title = (this.renameText || '').trim();

    this.renamingId = null;
    this.renameText = '';

    if (!title || title === chat.title) return;

    this.renameChat.emit({ chat, title });
  }

  /**
   * Cierra la caja de renombrado sin guardar.
   */
  cancelRename(): void {
    this.renamingId = null;
    this.renameText = '';
  }

  /**
   * Pide eliminar una conversación.
   * @param {ChatSummary} chat Conversación a eliminar
   * @param {Event} event Evento del clic, para no abrir la conversación
   */
  remove(chat: ChatSummary, event?: Event): void {
    event?.stopPropagation();

    this.deleteChat.emit(chat);
  }

  /**
   * Fecha de una conversación en formato corto: la hora si fue hoy, el día si no.
   * @param {string} iso Fecha en ISO
   * @returns {string} Devuelve la fecha lista para mostrar
   */
  chatDate(iso: string): string {
    if (!iso) return '';

    const date = new Date(iso);

    if (isNaN(date.getTime())) return '';

    const locale = this._translateService.currentLang === 'en' ? 'en-US' : 'es-CL';
    const sameDay = date.toDateString() === new Date().toDateString();

    return sameDay
      ? date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
  }

  /**
   * Identidad para el *ngFor de la lista.
   * @param {number} _index Posición en la lista
   * @param {ChatSummary} chat Conversación de la lista
   * @returns {string} Devuelve el id de la conversación
   */
  trackByChat(_index: number, chat: ChatSummary): string {
    return chat.id;
  }

  /**
   * Identidad para el *ngFor de los grupos. El getter `groups` arma objetos
   * nuevos en cada ciclo; sin esto Angular recrea toda la lista y la caja de
   * renombrado pierde el foco apenas se toca.
   * @param {number} _index Posición en la lista
   * @param {ChatHistoryGroup} group Grupo de la lista
   * @returns {string} Devuelve la etiqueta del grupo
   */
  trackByGroup(_index: number, group: ChatHistoryGroup): string {
    return group.label;
  }

  /**
   * Etiqueta localizada sin depender de claves nuevas del backend de traducciones.
   * @param {string} es Texto en español
   * @param {string} en Texto en inglés
   * @returns {string} Texto según el idioma activo
   */
  label(es: string, en: string): string {
    return this._translateService.currentLang === 'en' ? en : es;
  }
}
